// ═══════════════════════════════════════════════════════════════════════════
// DonorDetailView.jsx — v4.0.0 (Modularized Orchestrator)
// Two-mode member profile: 2-Column (sidebar + main) | Classic (original)
// Auto-detects screen size; user override remembered in sessionStorage
// Path: src/views/DonorDetailView.jsx
//
// v4.0.0 — Modularization complete (Phases 1–4)
//   Extracted to src/components/memberDetail/:
//     DonorDetailShared     — Field, SectionHead, AccentSection, etc.
//     ClassificationSection  — pathway, persona, flags, committees, prefs
//     CultivationSection     — score tiles, bar, persona guidance, next step
//     CommunicationSection   — contact log list
//     CustomFieldsSection    — dynamic custom fields
//     DonorDetailSidebar    — 2-col sticky sidebar
//     DonorDetailMain       — 2-col scrollable main
//     DonorDetailClassic    — classic single-column layout
//
// v3.4.0 — Contact Preference Codes (noEmail, noMail, noPhone, noText)
// v3.2.0 — Gift & Receipting (DonorSidebarBlock, GiftsSection, QuickGiftModal)
// v3.1.0 — Committee multi-select (COMMITTEES exported)
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import {
  User,
  Save,
  Trash2,
  Edit3,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  LayoutList,
  Sprout,
} from "lucide-react";
import { HELP } from "../constants/helpContent";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { ViewLayout } from "../components/ui/ViewLayout";
import { ActionButton } from "../components/ui/ActionButton";
import { ConfirmDialog } from "../components/ui/Modal";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { DonorFlags } from "../components/donors/DonorParts";
import { QuickLogModal } from "../components/features/QuickLogModal";
import { QuickGiftModal } from "../components/features/QuickGiftModal";
import { ScoreAuditModal } from "../components/features/ScoreAuditModal";
import {
  calculateEngagement,
  getPathwayForScore,
  resolveEffectivePathway,
  getMemberActivities,
} from "../utils/engagementScoring";
import { getDaysColor } from "../components/donorDetail/DonorDetailShared";
import { DonorDetailSidebar } from "../components/donorDetail/DonorDetailSidebar";
import { DonorDetailMain } from "../components/donorDetail/DonorDetailMain";
import { DonorDetailClassic } from "../components/donorDetail/DonorDetailClassic";
import { supabase } from "../lib/supabase";
import { QuickTaskModal } from "../components/features/QuickTaskModal";

// Re-export for backward compatibility (CultivationCommsTab imports from here or from constants)
export { COMMITTEES } from "../constants/committees";

// ═══════════════════════════════════════════════════════════════════════════
// MAIN VIEW COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const DonorDetailView = () => {
  const {
    palette,
    selectedMember,
    goBack,
    goHome,
    updateMember,
    deleteMember,
    refreshMember,
    directors,
    members,
    addContactLog,
    deleteContactLog,
    pathways,
    personas,
    navigateToNextMember,
    navigateToPrevMember,
    memberPosition,
    filteredMembers,
    customFields,
    isAdmin,
    isDirector,
    isVolunteer,
    userProfile,
    chapterSettings,
    activeFunds,
    canViewGifts,
    canFileContactReports,
    can,
    bumpDataVersion,
    cultivationQueue,
    addToQueue,
    showMessage,
    navigate,
    navContext,
    allGifts,
    allEventAttendance,
    allSponsorships,
    addTask,
    logContactRequestKey,
  } = useApp();

  // ── State ──────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [detailForm, setDetailForm] = useState({});
  const [showDelete, setShowDelete] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [editLogEntry, setEditLogEntry] = useState(null);
  const [showQuickGift, setShowQuickGift] = useState(false);
  const [deleteLogId, setDeleteLogId] = useState(null);
  const [showScoreAudit, setShowScoreAudit] = useState(false);
  const [showQuickTask, setShowQuickTask] = useState(false);

  const isInQueue = cultivationQueue.some((q) => q.memberId === selectedMember?.id);

  const [userOverride, setUserOverride] = useState(() => {
    try { return sessionStorage.getItem("cmm_detail_view") || null; } catch { return null; }
  });
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [editingSection, setEditingSection] = useState(null);
  const [collapsed2Col, setCollapsed2Col] = useState({
    custom: true, personal: true, professional: true,
    membership: true, address: true, gifts: true,
  });

  // ── Effects ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedMember) {
      setDetailForm({ ...selectedMember });
      setEditing(false);
      setEditingSection(null);

      // Fetch days since last donation from gifts table
      supabase
        .from("gifts")
        .select("gift_date")
        .eq("member_id", selectedMember.id)
        .order("gift_date", { ascending: false })
        .limit(1)
        .then(({ data }) => {
          if (data && data.length > 0 && data[0].gift_date) {
            const lastDate = new Date(data[0].gift_date + "T12:00:00");
            const days = Math.floor((new Date() - lastDate) / (1000 * 60 * 60 * 24));
            setDetailForm((prev) => ({
              ...prev,
              _daysSinceDonation: `${days} day${days !== 1 ? "s" : ""}`,
            }));
          }
        });
    }
  }, [selectedMember]);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Respond to the /log slash command — counter bumps each time the
  // user fires /log, so we ignore the initial 0 value.
  useEffect(() => {
    if (!logContactRequestKey) return;
    setShowLog(true);
  }, [logContactRequestKey]);

  if (!selectedMember) return null;

  // ── View mode ──────────────────────────────────────────────────────────
  const autoView = screenWidth >= 900 ? "twocol" : "classic";
  const viewMode = userOverride || autoView;

  const setViewMode = (mode) => {
    setUserOverride(mode);
    try { sessionStorage.setItem("cmm_detail_view", mode); } catch {}
  };

  // ── Permissions ────────────────────────────────────────────────────────
  const directorName = userProfile?.full_name || "";
  const isMyAssignment = selectedMember.assignedDirector === directorName;
  const canEdit = isAdmin || (isDirector && isMyAssignment);
  const canDelete = isAdmin;
  const canLogContact = can("logContacts") && (isAdmin || isMyAssignment);

  // ── Form helpers ───────────────────────────────────────────────────────
  const updateField = (key, value) =>
    setDetailForm((prev) => ({ ...prev, [key]: value }));

  // ── Engagement & pathway ───────────────────────────────────────────────
  const memberActivities = getMemberActivities(selectedMember?.id, allGifts, allEventAttendance, allSponsorships);
  const engagement = calculateEngagement(detailForm, undefined, memberActivities);
  const engPathway = getPathwayForScore(engagement.score);
  const memberLifetimeGiving = (memberActivities.memberGifts || []).reduce((sum, g) => sum + (parseFloat(g.amount) || 0), 0);
  const effective = resolveEffectivePathway(detailForm, engagement.score, pathways, undefined, { lifetimeGiving: memberLifetimeGiving });
  const scoreColor =
    engagement.score >= 80 ? "#10b981"
    : engagement.score >= 30 ? "#3b82f6"
    : engagement.score >= 10 ? "#f59e0b"
    : "#ef4444";
  const daysColor = getDaysColor(engagement.daysSinceContact);
  const scorePct = Math.min((engagement.score / 150) * 100, 100);

  // ── Persona lookup ─────────────────────────────────────────────────────
  const selectedPersona =
    detailForm.persona && (personas || []).length > 0
      ? personas.find((p) => p.id === detailForm.persona)
      : null;

  // ── Suggested next move ────────────────────────────────────────────────
  let suggestion = "Schedule check-in to maintain relationship";
  const logs = detailForm.contactLog || [];
  if (detailForm.isDonor && engagement.daysSinceContact !== null && engagement.daysSinceContact > 60)
    suggestion = "Stewardship touch — thank and update on chapter impact";
  else if (detailForm.isDonor && engagement.daysSinceContact === null)
    suggestion = "Stewardship touch — thank and update on chapter impact";
  else if (detailForm.isBoardMember && !detailForm.isDonor)
    suggestion = "Board peer ask — personal conversation about giving";
  else if (engagement.score >= 40 && !detailForm.isDonor)
    suggestion = "Engagement is strong — consider a personal giving conversation";
  else if (engagement.score >= 10 && engagement.daysSinceContact !== null && engagement.daysSinceContact > 45)
    suggestion = "Re-engage with event invitation or coffee meeting";
  else if (!detailForm.assignedDirector?.trim())
    suggestion = "Assign a board member to build relationship";
  else if (logs.length === 0)
    suggestion = "Make first contact — welcome email or intro call";
  else if (engagement.daysSinceContact !== null && engagement.daysSinceContact > 30)
    suggestion = `${engagement.daysSinceContact} days since contact — reconnect this week`;

  // ── Section editing helpers ────────────────────────────────────────────
  const fieldProps = { editing, detailForm, updateField, palette };

  const sectionFieldProps = (sectionKey) => ({
    editing: editingSection === sectionKey,
    detailForm, updateField, palette,
  });

  const startSectionEdit = (section) => {
    if (editingSection && editingSection !== section) return;
    setEditingSection(section);
  };

  const cancelSectionEdit = () => {
    if (selectedMember) setDetailForm({ ...selectedMember });
    setEditingSection(null);
  };

  const saveSectionEdit = (section, fields) => {
    const updates = {};
    fields.forEach((f) => {
      if (detailForm[f] !== undefined) updates[f] = detailForm[f];
    });
    if (Object.keys(updates).length > 0)
      updateMember(selectedMember.id, updates, true);
    setEditingSection(null);
  };

  const toggle2Col = (key) =>
    setCollapsed2Col((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Gift saved callback ────────────────────────────────────────────────
  const handleGiftSaved = useCallback(() => {
    refreshMember(selectedMember.id);
    bumpDataVersion();
  }, [selectedMember?.id, refreshMember, bumpDataVersion]);

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <ViewLayout>
      {/* ── Zone 1: Header ──────────────────────────────────────────────── */}
      <ZoneOneHeader
        title={
          <>
            {(detailForm.donorType || selectedMember?.donorType) === "organization"
              ? detailForm.organization || selectedMember?.organization || "Organization"
              : (detailForm.donorType || selectedMember?.donorType) === "household"
                ? detailForm.householdName || `${detailForm.firstName || ""} ${detailForm.lastName || ""}`.trim() || "Household"
                : `${detailForm.firstName || ""} ${detailForm.lastName || ""}`.trim() || detailForm.credentialedFullName || "Donor"}
            {selectedMember?.id && (
              <span style={{ fontSize: "0.45rem", fontWeight: 400, opacity: 0.6, marginLeft: 8 }}>
                {selectedMember.cpDonorId || selectedMember.spektrixId || selectedMember.id.slice(0, 8)}
              </span>
            )}
          </>
        }
        subtitle={
          (detailForm.donorType || selectedMember?.donorType) === "organization"
            ? [detailForm.firstName, detailForm.lastName].filter(Boolean).join(" ") || null
            : detailForm.organization
        }
        icon={User}
        helpContent={HELP.donorDetail}
        onBack={goBack}
        onHome={goHome}
      />
      <ZoneTwoToolbar />

      {/* ── Zone 2: Toolbar ─────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 24px", background: palette.toolbarBg,
          borderBottom: `1px solid ${palette.cardBorder}`,
          flexWrap: "wrap", gap: 8,
        }}
      >
        {/* Left: View toggle */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 2,
            background: `${palette.accent1}08`, borderRadius: 8, padding: 2,
          }}
        >
          {[
            { key: "twocol", icon: LayoutDashboard, label: "2-Col" },
            { key: "classic", icon: LayoutList, label: "Classic" },
          ].map((v) => (
            <button
              key={v.key}
              onClick={() => setViewMode(v.key)}
              title={v.label}
              style={{
                display: "flex", alignItems: "center", gap: 4,
                padding: "5px 10px", borderRadius: 6, border: "none",
                fontSize: FONT_SIZES.xs, fontWeight: 600, fontFamily: FONT_FAMILY,
                background: viewMode === v.key ? effective.color : "transparent",
                color: viewMode === v.key ? "#fff" : palette.text3,
                cursor: "pointer", transition: "all 0.15s",
              }}
            >
              <v.icon size={ICON_SIZES.sm} />
              {v.label}
            </button>
          ))}
          {userOverride && (
            <button
              onClick={() => {
                setUserOverride(null);
                try { sessionStorage.removeItem("cmm_detail_view"); } catch {}
              }}
              title="Reset to auto-detect"
              style={{
                padding: "3px 6px", borderRadius: 4,
                border: `1px solid ${palette.cardBorder}`,
                background: "transparent", color: palette.text3,
                fontSize: 10, cursor: "pointer", marginLeft: 4,
              }}
            >
              ↺
            </button>
          )}
        </div>

        {/* Center: Donor ID + Record Navigation */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {selectedMember?.id && (
            <span
              style={{
                fontSize: "0.55rem",
                fontWeight: 600,
                color: palette.text3,
                fontFamily: FONT_FAMILY,
                padding: "2px 8px",
                borderRadius: 6,
                background: `${palette.text3}10`,
                marginRight: 6,
                letterSpacing: "0.02em",
              }}
              title={`UUID: ${selectedMember.id}`}
            >
              {selectedMember.spektrixId
                ? `Spektrix: ${selectedMember.spektrixId}`
                : selectedMember.cpDonorId
                  ? `CP: ${selectedMember.cpDonorId}`
                  : `ID: ${selectedMember.id.slice(0, 8)}`}
            </span>
          )}
          {viewMode === "classic" && <DonorFlags member={detailForm} effective={effective} />}
          {filteredMembers.length > 1 && (
            <div
              style={{
                display: "flex", alignItems: "center", gap: 4,
                marginLeft: viewMode === "classic" ? 12 : 0,
              }}
            >
              <button
                onClick={navigateToPrevMember}
                disabled={memberPosition <= 1}
                title="Previous member"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 32, height: 32, borderRadius: 8,
                  border: `1px solid ${palette.inputBorder}`,
                  background: memberPosition <= 1 ? "transparent" : palette.inputBg,
                  cursor: memberPosition <= 1 ? "default" : "pointer",
                  opacity: memberPosition <= 1 ? 0.3 : 1, transition: "all 0.15s",
                }}
              >
                <ChevronLeft size={ICON_SIZES.sm} style={{ color: palette.text1 }} />
              </button>
              <span
                style={{
                  fontSize: FONT_SIZES.xs, color: palette.text2, fontWeight: 600,
                  minWidth: 60, textAlign: "center", fontFamily: FONT_FAMILY,
                }}
              >
                {memberPosition} of {filteredMembers.length}
              </span>
              <button
                onClick={navigateToNextMember}
                disabled={memberPosition >= filteredMembers.length}
                title="Next member"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 32, height: 32, borderRadius: 8,
                  border: `1px solid ${palette.inputBorder}`,
                  background: memberPosition >= filteredMembers.length ? "transparent" : palette.inputBg,
                  cursor: memberPosition >= filteredMembers.length ? "default" : "pointer",
                  opacity: memberPosition >= filteredMembers.length ? 0.3 : 1, transition: "all 0.15s",
                }}
              >
                <ChevronRight size={ICON_SIZES.sm} style={{ color: palette.text1 }} />
              </button>
            </div>
          )}
        </div>

        {/* Right: Edit Controls */}
        <div style={{ display: "flex", gap: 8 }}>
          {editing ? (
            <>
              <ActionButton
                label="Cancel" variant="secondary" small
                onClick={() => { setDetailForm({ ...selectedMember }); setEditing(false); }}
              />
              <ActionButton
                label="Save" icon={Save} small
                onClick={() => { updateMember(selectedMember.id, detailForm); setEditing(false); }}
              />
            </>
          ) : (
            <>
              <ActionButton
                label={isInQueue ? "Queued" : "Cultivate"}
                icon={Sprout}
                variant={isInQueue ? "secondary" : "primary"}
                small
                disabled={isInQueue}
                onClick={() => {
                  addToQueue(selectedMember.id, "manual");
                  goBack();
                }}
              />
              {canEdit && (
                <ActionButton label="Edit" icon={Edit3} variant="secondary" small onClick={() => setEditing(true)} />
              )}
              {canDelete && (
                <ActionButton label="Delete" icon={Trash2} variant="danger" small onClick={() => setShowDelete(true)} />
              )}
              {!canEdit && !canDelete && (
                <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3, fontStyle: "italic" }}>Read-only</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Layout ──────────────────────────────────────────────────────── */}
      {viewMode === "twocol" ? (
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <DonorDetailSidebar
            detailForm={detailForm}
            engagement={engagement}
            scoreColor={scoreColor}
            daysColor={daysColor}
            scorePct={scorePct}
            effective={effective}
            palette={palette}
            pathways={pathways}
            personas={personas}
            directors={directors}
            members={members}
            selectedMember={selectedMember}
            chapterSettings={chapterSettings}
            canEdit={canEdit}
            canLogContact={canLogContact}
            canViewGifts={canViewGifts}
            updateField={updateField}
            updateMember={updateMember}
            onLogContact={() => setShowLog(true)}
            onAddGift={() => setShowQuickGift(true)}
            onShowScoreAudit={() => setShowScoreAudit(true)}
            onAddTask={() => setShowQuickTask(true)}
            navigate={navigate}
            showMessage={showMessage}
          />
          <DonorDetailMain
            key={selectedMember?.id}
            detailForm={detailForm}
            selectedMember={selectedMember}
            palette={palette}
            effective={effective}
            engagement={engagement}
            engPathway={engPathway}
            scoreColor={scoreColor}
            daysColor={daysColor}
            scorePct={scorePct}
            selectedPersona={selectedPersona}
            suggestion={suggestion}
            canEdit={canEdit}
            canLogContact={canLogContact}
            canViewGifts={canViewGifts}
            canFileContactReports={canFileContactReports}
            updateField={updateField}
            updateMember={updateMember}
            handleGiftSaved={handleGiftSaved}
            onLogContact={() => setShowLog(true)}
            onEditLog={(entry) => setEditLogEntry(entry)}
            onDeleteLog={(id) => setDeleteLogId(id)}
            customFields={customFields}
            collapsed2Col={collapsed2Col}
            toggle2Col={toggle2Col}
            editingSection={editingSection}
            startSectionEdit={startSectionEdit}
            cancelSectionEdit={cancelSectionEdit}
            saveSectionEdit={saveSectionEdit}
            sectionFieldProps={sectionFieldProps}
            showMessage={showMessage}
          />
        </div>
      ) : (
        <DonorDetailClassic
          key={selectedMember?.id}
          detailForm={detailForm}
          selectedMember={selectedMember}
          palette={palette}
          effective={effective}
          engagement={engagement}
          engPathway={engPathway}
          scoreColor={scoreColor}
          daysColor={daysColor}
          scorePct={scorePct}
          selectedPersona={selectedPersona}
          suggestion={suggestion}
          canEdit={canEdit}
          canLogContact={canLogContact}
          canViewGifts={canViewGifts}
          canFileContactReports={canFileContactReports}
          updateField={updateField}
          updateMember={updateMember}
          handleGiftSaved={handleGiftSaved}
          onLogContact={() => setShowLog(true)}
          onEditLog={(entry) => setEditLogEntry(entry)}
          onDeleteLog={(id) => setDeleteLogId(id)}
          pathways={pathways}
          personas={personas}
          directors={directors}
          members={members}
          customFields={customFields}
          editing={editing}
          fieldProps={fieldProps}
          showMessage={showMessage}
          navigate={navigate}
        />
      )}

      <ZoneThreeFooter />

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {showDelete && (
        <ConfirmDialog
          title="Delete Donor?"
          message={`Permanently remove ${detailForm.firstName} ${detailForm.lastName}?`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => { deleteMember(selectedMember.id); goBack(); }}
          onCancel={() => setShowDelete(false)}
        />
      )}
      {(showLog || editLogEntry) && (
        <QuickLogModal
          member={selectedMember}
          onClose={() => { setShowLog(false); setEditLogEntry(null); }}
          initialType="email"
          editEntry={editLogEntry}
          defaultPurpose={!editLogEntry && navContext?.from === "renewals" ? ["gift_renewal"] : null}
          defaultTemplate={!editLogEntry ? navContext?.suggestedTemplate || null : null}
        />
      )}
      {deleteLogId && (
        <ConfirmDialog
          title="Delete Log Entry?"
          message="Remove this communication record?"
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => { deleteContactLog(selectedMember.id, deleteLogId); setDeleteLogId(null); }}
          onCancel={() => setDeleteLogId(null)}
        />
      )}
      {canViewGifts && showQuickGift && (
        <QuickGiftModal
          member={selectedMember}
          onClose={() => setShowQuickGift(false)}
          onSaved={handleGiftSaved}
        />
      )}
      {showScoreAudit && (
        <ScoreAuditModal
          member={selectedMember}
          engagement={engagement}
          onClose={() => setShowScoreAudit(false)}
        />
      )}
      {showQuickTask && (
        <QuickTaskModal
          onClose={() => setShowQuickTask(false)}
          onSave={async (taskData) => {
            await addTask(taskData);
            setShowQuickTask(false);
          }}
          directors={directors}
          members={members}
          defaultDirector={selectedMember?.assignedDirector || ""}
          defaultMemberId={selectedMember?.id || null}
        />
      )}
    </ViewLayout>
  );
};